import { MobileFilter } from "src/molecules/MobileFilter";
import {
  MobileActiveFilters,
  MobileFilterOptionButton,
} from "./MobileFilters.styled";
import { MessageDescriptor } from "@lingui/core";
import {
  MutableRefObject,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useLingui } from "@lingui/react";
import { ArrowRightIcon } from "src/icons";
import { Text } from "@otrium/atoms";
import { FilterType } from "./MobileFilters";
import { MobileBrandFilter } from "src/molecules/MobileFilter/MobileBrandFilter";
import { MobileCategoryFilter } from "src/molecules/MobileFilter/MobileCategoryFilter";
import { PLPType } from "src/types/PLPType.d";
import {
  useClearRefinements,
  useCurrentRefinements,
} from "react-instantsearch-core-v7";
import {
  CATEGORIES_FACET,
  CATEGORY_PAGE_ID_FACET,
  PRICE_FACET,
} from "src/utils/algolia";
import {
  queryStringKeyMap,
  useRemoveFilters,
} from "src/hooks/use-handle-search-params";
import { theme } from "src/theme";
import { useSegment } from "src/hooks/useSegment";

type MobileFilterOptionProps = {
  title: MessageDescriptor;
  attribute: string;
  icon: ReactNode;
  type: FilterType;
  isMultiColumn: boolean;
  categoryPageId: string;
  gender: string;
  pageContentType: PLPType;
  gridRef: MutableRefObject<HTMLDivElement | null>;
  handleScrollToTop: () => void;
};

function MobileFilterOption({
  title,
  attribute,
  icon,
  type,
  isMultiColumn,
  categoryPageId,
  gender,
  pageContentType,
  gridRef,
  handleScrollToTop,
}: MobileFilterOptionProps): JSX.Element {
  const { _ } = useLingui();
  const [isOpen, setOpen] = useState(false);
  const [canRefine, setCanRefine] = useState(true);
  const { items: currentRefinements } = useCurrentRefinements({
    includedAttributes: [
      attribute === CATEGORIES_FACET ? CATEGORY_PAGE_ID_FACET : attribute,
    ],
  });
  const { refine: clear } = useClearRefinements({
    includedAttributes: [
      attribute === CATEGORIES_FACET ? CATEGORY_PAGE_ID_FACET : attribute,
    ],
  });
  const { segmentProductListFilterOpened } = useSegment();

  const { removePriceFilters, removeAllFiltersByKey } = useRemoveFilters();

  const handleClear = useCallback(() => {
    clear();

    if (attribute === PRICE_FACET) {
      removePriceFilters();
    }

    removeAllFiltersByKey(queryStringKeyMap[attribute]);
  }, [attribute, clear, removePriceFilters, removeAllFiltersByKey]);

  const refinements = useMemo(
    () =>
      currentRefinements
        .flatMap((item) =>
          item.refinements.flatMap((value) =>
            attribute === CATEGORIES_FACET
              ? value.label.split(" > ").at(-1) || ""
              : value.label.split("||").at(1) || ""
          )
        )
        .join(", "),
    [attribute, currentRefinements]
  );

  const hasFilters = useMemo(
    () => currentRefinements.flatMap((item) => item.refinements).length > 0,
    [currentRefinements]
  );

  const toggleOpen = useCallback(() => {
    setOpen((prevState) => {
      if (!prevState) {
        void segmentProductListFilterOpened(
          attribute === CATEGORIES_FACET ? CATEGORY_PAGE_ID_FACET : attribute
        );
      }

      return !prevState;
    });
  }, [attribute, segmentProductListFilterOpened]);

  return (
    <>
      <MobileFilterOptionButton onClick={toggleOpen} disabled={!canRefine}>
        {icon}
        <Text minWidth="fit-content" color={theme.colors.tone.black}>
          {_(title)}
        </Text>
        {hasFilters ? (
          <MobileActiveFilters>{refinements}</MobileActiveFilters>
        ) : null}
        <ArrowRightIcon />
      </MobileFilterOptionButton>

      {type === "brands" ? (
        <MobileBrandFilter
          title={title}
          attribute={attribute}
          isOpen={isOpen}
          searchPlaceholder={_({ id: "Search brand" })}
          toggleOpen={toggleOpen}
          showSearch
          isMultiColumn={isMultiColumn || false}
          setCanRefine={setCanRefine}
          onClear={handleClear}
          hasFilters={hasFilters}
          handleScrollToTop={handleScrollToTop}
        />
      ) : null}

      {type === "categories" ? (
        <MobileCategoryFilter
          title={title}
          isOpen={isOpen}
          toggleOpen={toggleOpen}
          categoryPageId={categoryPageId}
          gender={gender}
          pageContentType={pageContentType}
          onClear={handleClear}
          hasFilters={hasFilters}
          gridRef={gridRef}
        />
      ) : null}

      {type !== "brands" && type !== "range" && type !== "categories" ? (
        <MobileFilter
          title={title}
          attribute={attribute}
          isOpen={isOpen}
          toggleOpen={toggleOpen}
          type={type}
          isMultiColumn={isMultiColumn}
          setCanRefine={setCanRefine}
          onClear={handleClear}
          hasFilters={hasFilters}
          handleScrollToTop={handleScrollToTop}
        />
      ) : null}
    </>
  );
}

export { MobileFilterOption };
